"use client";

// Fires GA scroll-depth events at 25/50/75/100% so we can see how far visitors
// actually get on each page (engagement / where they drop). Thresholds reset on
// route change. No-ops if gtag hasn't loaded (it's lazyOnload).
import { useEffect } from "react";
import { usePathname } from "next/navigation";

const THRESHOLDS = [25, 50, 75, 100] as const;

function gtag(...args: unknown[]) {
  (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag?.(...args);
}

export function ScrollDepthTracker() {
  const pathname = usePathname() ?? "/";

  useEffect(() => {
    const fired = new Set<number>();
    let ticking = false;

    const check = () => {
      ticking = false;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.min(100, Math.round(((window.scrollY || doc.scrollTop) / scrollable) * 100));
      for (const t of THRESHOLDS) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          gtag("event", "scroll_depth", { percent: t, page_path: pathname });
        }
      }
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(check);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    check(); // capture short pages that are already fully visible
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  return null;
}
